/**
 * Portfolio vocabulary — the option lists the student picks from.
 *
 * These mirror the enums in vc_be/models/StudentPortfolio.js. They live here
 * rather than in each form because three surfaces render them (the student's
 * editor, the staff read-only view, and the resume report's context strip) and a
 * seniority that reads "mid" in one and "Mid-level" in another is the kind of
 * drift nobody notices until a screenshot goes to a customer.
 */

export const ROLE_FAMILIES = [
  { value: "", label: "Not sure yet" },
  { value: "engineering", label: "Engineering" },
  { value: "data", label: "Data & Analytics" },
  { value: "design", label: "Design" },
  { value: "product", label: "Product" },
  { value: "marketing", label: "Marketing" },
  { value: "sales", label: "Sales" },
  { value: "operations", label: "Operations" },
  { value: "finance", label: "Finance" },
  { value: "hr", label: "People & HR" },
  { value: "other", label: "Other" },
];

export const SENIORITY_LEVELS = [
  { value: "", label: "Not specified" },
  { value: "intern", label: "Internship" },
  { value: "fresher", label: "Fresher / Entry level" },
  { value: "junior", label: "Junior (0-2 years)" },
  { value: "mid", label: "Mid-level (2-5 years)" },
  { value: "senior", label: "Senior (5+ years)" },
  { value: "lead", label: "Lead / Manager" },
];

export const SKILL_LEVELS = [
  { value: "beginner", label: "Beginner" },
  { value: "intermediate", label: "Intermediate" },
  { value: "advanced", label: "Advanced" },
  { value: "expert", label: "Expert" },
];

export const SKILL_CATEGORIES = [
  { value: "technical", label: "Technical" },
  { value: "tool", label: "Tool / Platform" },
  { value: "soft", label: "Soft skill" },
  { value: "language", label: "Language" },
  { value: "domain", label: "Domain knowledge" },
];

export const ACHIEVEMENT_TYPES = [
  { value: "award", label: "Award" },
  { value: "hackathon", label: "Hackathon" },
  { value: "competition", label: "Competition" },
  { value: "publication", label: "Publication" },
  { value: "scholarship", label: "Scholarship" },
  { value: "volunteering", label: "Volunteering" },
  { value: "other", label: "Other" },
];

/** Look a value up in one of the lists above, falling back to the raw value. */
export const labelFor = (options, value) =>
  options.find((o) => o.value === value)?.label || value || "—";

/**
 * A score's tone, and the words that go with it.
 *
 * ⚠️ Bands, not a gradient. A number on its own invites a student to read 71 as
 * meaningfully better than 68, which the model cannot support — it is one
 * language model's read of one document. Four bands say what the number is
 * actually good for: which pile the resume is in, and whether to keep working.
 *
 * The tones are the status families from the closed palette. `--chart-N` would
 * render correctly and still be wrong: a resume score is a judgement, not a
 * chart series.
 */
export const scoreBand = (score) => {
  if (score === null || score === undefined) return { tone: "muted", label: "Not scored" };
  if (score >= 80) return { tone: "success", label: "Strong" };
  if (score >= 65) return { tone: "info", label: "Good" };
  if (score >= 45) return { tone: "warning", label: "Needs work" };
  return { tone: "danger", label: "Needs a rewrite" };
};

/** Tailwind classes per tone. Written once so a band is coloured identically everywhere. */
export const TONE_CLASSES = {
  success: { text: "tw-text-success", bg: "tw-bg-success/10", ring: "tw-text-success", border: "tw-border-success/30" },
  info: { text: "tw-text-info", bg: "tw-bg-info/10", ring: "tw-text-info", border: "tw-border-info/30" },
  warning: { text: "tw-text-warning", bg: "tw-bg-warning/10", ring: "tw-text-warning", border: "tw-border-warning/30" },
  danger: { text: "tw-text-danger", bg: "tw-bg-danger/10", ring: "tw-text-danger", border: "tw-border-danger/30" },
  muted: { text: "tw-text-content-subtle", bg: "tw-bg-surface-sunken", ring: "tw-text-content-subtle", border: "tw-border-line-soft" },
};

/**
 * What a failed analysis means and what to do about it.
 *
 * Keyed by `ResumeAnalysis.errorCode`. A scanned PDF is a dead end the student
 * can fix in two minutes and a provider outage is one they can only wait out —
 * showing the same "analysis failed" for both sends half of them to support.
 */
export const ANALYSIS_ERROR_HELP = {
  no_text:
    "No readable text was found in this file. Scanned or photographed resumes cannot be analysed — export a text-based PDF from Word, Google Docs or your resume builder and upload that.",
  parse_failed:
    "This file could not be read. Try re-exporting it as a PDF and uploading again.",
  file_missing:
    "The uploaded file could not be found in storage. Please upload your resume again.",
  no_resume: "Upload a resume first.",
  ai_unavailable:
    "AI analysis is not switched on for your institute. Ask your administrator to enable it.",
  bad_response:
    "The analysis came back incomplete. Please try running it again.",
  ai_error:
    "The analysis could not be completed. Please try again in a few minutes.",
};

/** A YouTube/Vimeo thumbnail from the id the server extracted on write. */
export const videoThumbnail = (video) => {
  if (!video) return null;
  if (video.provider === "youtube" && video.videoId) {
    return `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`;
  }
  return null;
};

/** Human date, IST — the box runs UTC and a bare toLocaleDateString names the wrong day. */
export const fmtDate = (d, opts = {}) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    year: "numeric", month: "short", day: "numeric",
    timeZone: "Asia/Kolkata", ...opts,
  });
};

/** "Mar 2024 — Present" for a project's date range. */
export const dateRange = (start, end, ongoing) => {
  const f = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { year: "numeric", month: "short", timeZone: "Asia/Kolkata" }) : "");
  const from = f(start);
  const to = ongoing ? "Present" : f(end);
  if (!from && !to) return "";
  if (!to) return from;
  if (!from) return to;
  return `${from} — ${to}`;
};
